import { apiFetch } from './apiFetch';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/MobileDevice`;

const buildQueryString = (params) =>
  Object.entries(params)
    .filter(([, val]) => val !== undefined && val !== null && val !== "")
    .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
    .join('&');

const parseResponseBody = async (response) => response.json().catch(() => null);

const throwIfNotOk = async (response, url) => {
  if (response.ok) {
    return parseResponseBody(response);
  }

  const body = await parseResponseBody(response);
  const err = new Error(body?.message || `HTTP ${response.status}`);
  err.status = response.status;
  err.body = body;
  throw err;
};

const getDevices = async ({
  pageSize = 20,
  pageIndex = 1,
  sortBy,
  sortAsc = true,
  authToken,
  ...filters
}) => {
  try {
    const query = buildQueryString(filters);
    const url = `${API_BASE_URL}/GetMobileDeviceActivityInfo?${query}`;

    const response = await apiFetch(url, {
      headers: {        
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return { data: [], totalPages: 0, totalCount: 0 };
    }

    const json = await response.json();

    let devices = json.map((item) => {
      const parsedData = JSON.parse(item.data);
      return {
        groupName: item.groupName,
        visorId: item.visorId,
        version: item.version,
        osVersion: `Android ${item.os}`,
        lastOnline: item.lastOnline,
        installationDate: item.installationDate,
        heartbeat: {
          visorId: item.visorId,
          manufacturer: parsedData.hardwareInfo?.manufacturer || '',
          phoneModel: parsedData.hardwareInfo?.model || '',
          osVersion: `Android ${parsedData.hardwareInfo?.version || ''}`,
          forcedStop: item.forcedStop || false,
          forcedStopDate: item.forcedStopDate,
          esafeVersion: parsedData.application_version || '',
          timestamp: new Date(parsedData.timestamp).toISOString(),
          isOnline: parsedData.core_services?.capture_service_running || false,
        },
      };
    });

    if (filters.groupName) {
      devices = devices.filter((d) =>
        d.groupName.toLowerCase().includes(filters.groupName.toLowerCase())
      );
    }

    if (sortBy) {
      devices.sort((a, b) => {
        const valA = a[sortBy] || '';
        const valB = b[sortBy] || '';
        return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });
    }

    const start = (pageIndex - 1) * pageSize;
    const paged = devices.slice(start, start + pageSize);
    const totalPages = Math.ceil(devices.length / pageSize);

    return {
      data: paged,
      totalPages,
      totalCount: devices.length,
    };
  } catch (error) {
    return { data: [], totalPages: 0, totalCount: 0 };
  }
};

const getMobileHeartbeatByUser = async ({ interval, transgressorId, authToken }) => {
  try {
    const query = buildQueryString({ interval, transgressorId });
    const url = `${process.env.REACT_APP_API_URL}/MobileDevice/GetMobileDevicesHeartbeatByUser?${query}`;

    const response = await apiFetch(url, {
      headers: {        
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const json = await response.json();
    return json;
  } catch (error) {
    return [];
  }
};

const getMobileInfoByUser = async ({ transgressorId }) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/MobileDevice/GetMobileDeviceHeartbeatByUser?transgressorId=${transgressorId}`;
    const response = await apiFetch(url, {
      headers: {        
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const json = await response.json();
    return json;
  } catch (error) {
    return [];
  }
};

const VISOR_API_BASE = `${process.env.REACT_APP_API_URL}/Visor`;

const getVisorIdListMobile = async ({ groupId }) => {
  try {
    const url = `${VISOR_API_BASE}/GetVisorIdListMobile?groupid=${encodeURIComponent(groupId)}`;

    const response = await apiFetch(url, {
      headers: {        
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const json = await response.json();
    return json;
  } catch (error) {
    return [];
  }
};

const deleteVisor = async (visorId) => {
    const url = `${process.env.REACT_APP_API_URL}/Visor/DeleteVisor?visorId=${encodeURIComponent(visorId)}`;
    const response = await apiFetch(url, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const err = new Error(body?.message || `HTTP ${response.status}`);
      err.status = response.status;
      err.body = body;
      throw err;
    }

    return { isSuccess: true };
  };

const getDeviceTelemetry = async ({ visorRef, fromDate, toDate }) => {
  const trimmedVisorRef = visorRef?.trim();

  if (!trimmedVisorRef) {
    return [];
  }

  if (!process.env.REACT_APP_API_URL) {
    const configError = new Error("Telemetry API is not configured. Set REACT_APP_API_URL.");
    configError.code = "TELEMETRY_API_NOT_CONFIGURED";
    throw configError;
  }

  const trimmedApiUrl = `${process.env.REACT_APP_API_URL}`.replace(/\/+$/, "");
  const telemetryApiBase = trimmedApiUrl.endsWith("/v1")
    ? `${trimmedApiUrl}/MobileDevice`
    : `${trimmedApiUrl}/v1/MobileDevice`;

  const query = buildQueryString({
    deviceId: trimmedVisorRef,
    startDate: fromDate,
    endDate: toDate,
  });
  const url = `${telemetryApiBase}/GetDeviceTelemetry?${query}`;
  const response = await apiFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(body?.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    error.url = url;
    throw error;
  }

  try {
    const json = await response.json();
    return json;
  } catch (error) {
    const parseError = new Error("Telemetry API returned a non-JSON response.");
    parseError.cause = error;
    parseError.url = url;
    throw parseError;
  }
};

const FAVOURITES_API_BASE = `${process.env.REACT_APP_API_URL}/Favourites`;

const setFavourites = async (payload) => {
  const url = `${FAVOURITES_API_BASE}/SetFavourites`;
  const response = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return throwIfNotOk(response, url);
};

const getFavourites = async ({ deviceId = '' } = {}) => {
  const url = `${FAVOURITES_API_BASE}/GetFavourites?deviceId=${encodeURIComponent(deviceId)}`;
  const response = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return throwIfNotOk(response, url);
};

const updateFavourites = async (payload) => {
  const url = `${FAVOURITES_API_BASE}/UpdateFavourites`;
  const response = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return throwIfNotOk(response, url);
};

const deleteFavourites = async (deviceId) => {
  const url = `${FAVOURITES_API_BASE}/DeleteFavourites?deviceId=${encodeURIComponent(deviceId)}`;
  const response = await apiFetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  return throwIfNotOk(response, url);
};

const deviceService = {
  getDevices,
  getMobileHeartbeatByUser,
  getMobileInfoByUser,
  getVisorIdListMobile,
  deleteVisor,
  setFavourites,
  getFavourites,
  updateFavourites,
  deleteFavourites,
  getDeviceTelemetry
};

export default deviceService;
